<template>
    <div class="tab-content">
        <div class="matrix-scroll-container">
            <table class="matrix-table">
                <colgroup>
                    <col class="col-resource" />
                    <col v-for="column in permissionColumns" :key="'col-' + column.key" />
                </colgroup>
                <thead>
                    <tr>
                        <th rowspan="2" class="sticky-col">
                            <div class="select-all-container">
                                <input
                                    type="checkbox"
                                    :checked="isAllSelected()"
                                    :indeterminate="isPartiallySelected()"
                                    @change="toggleAllPermissions($event.target.checked)"
                                    class="select-all-checkbox"
                                    title="전체 선택/해제"
                                />
                                <span class="select-all-label">리소스</span>
                            </div>
                        </th>
                        <th
                            v-for="role in roles"
                            :key="'role-' + role.roleIdx"
                            :colspan="uniqueActionsCount"
                            class="role-header"
                        >
                            <div class="role-header-wrapper">
                                <div class="role-select-container">
                                    <input
                                        type="checkbox"
                                        :checked="isRoleColumnFullySelected(role.roleIdx)"
                                        :indeterminate="isRoleColumnPartiallySelected(role.roleIdx)"
                                        @change="toggleRoleColumn(role.roleIdx, $event.target.checked)"
                                        :disabled="role.isSystem"
                                        class="role-select-checkbox"
                                        :title="`${role.roleName} 전체 선택/해제`"
                                    />
                                    <span>{{ role.roleName }}</span>
                                </div>
                                <div v-if="!role.isSystem" class="role-dropdown-wrapper">
                                    <CustomDropdown :items="getRoleDropdownItems(role)" align="right">
                                        <template #trigger>
                                            <button
                                                type="button"
                                                class="role-dropdown-btn"
                                                :title="`${role.roleName} 메뉴`"
                                            >
                                                <i class="fas fa-chevron-down"></i>
                                            </button>
                                        </template>
                                    </CustomDropdown>
                                </div>
                            </div>
                        </th>
                    </tr>
                    <tr>
                        <th
                            v-for="column in permissionColumns"
                            :key="column.key"
                            class="action-header"
                            :class="{ 'action-header-first': column.isFirstActionOfRole }"
                        >
                            {{ column.label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="resource in resources" :key="resource.name">
                        <td class="sticky-col resource-name">
                            <div class="resource-header-wrapper">
                                <div class="resource-select-container">
                                    <input
                                        type="checkbox"
                                        :checked="isResourceRowFullySelected(resource.name)"
                                        :indeterminate="isResourceRowPartiallySelected(resource.name)"
                                        @change="toggleResourceRow(resource.name, $event.target.checked)"
                                        class="resource-select-checkbox"
                                        :title="`${resource.label} 전체 선택/해제`"
                                    />
                                    <span>{{ resource.label }}</span>
                                </div>
                                <div class="resource-dropdown-wrapper">
                                    <CustomDropdown :items="getResourceDropdownItems(resource)" align="left">
                                        <template #trigger>
                                            <button
                                                type="button"
                                                class="resource-dropdown-btn"
                                                :title="`${resource.label} 메뉴`"
                                            >
                                                <i class="fas fa-chevron-down"></i>
                                            </button>
                                        </template>
                                    </CustomDropdown>
                                </div>
                            </div>
                        </td>
                        <td
                            v-for="column in permissionColumns"
                            :key="column.key"
                            class="permission-cell"
                            :class="{
                                changed: isChanged(column.roleIdx, resource.name, column.action),
                            }"
                        >
                            <input
                                type="checkbox"
                                :checked="hasPermission(column.roleIdx, resource.name, column.action)"
                                @change="
                                    togglePermission(column.roleIdx, resource.name, column.action, $event)
                                "
                                :disabled="column.isSystem"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "../../../codes";
import CustomDropdown from "../../common/CustomDropdown.vue";
import accessControlMixin from "../mixins/accessControlMixin";

export default {
    name: "PermissionMatrixTab",
    components: { CustomDropdown },
    mixins: [accessControlMixin],
    props: {
        roles: { type: Array, default: () => [] },
        resources: { type: Array, default: () => [] },
    },
    data() {
        return {
            permissionMatrix: {},
            originalPermissionMatrix: {},
            loadingPermissions: false,
            savingPermissions: false,
            hiddenActions: [],
            copiedRolePermissions: null,
        };
    },
    computed: {
        actionOrder() {
            const list = CarbonCodes.permissionAction.getList();
            return list.length
                ? list.map((c) => (c.value || "").toLowerCase())
                : ["read", "write", "delete", "activate", "verify", "admin"];
        },
        permissionColumns() {
            const isVisible = (action) => !this.hiddenActions.includes(action);
            const columns = [];
            const discoveredActions = new Set(this.actionOrder.filter(isVisible));
            Object.values(this.permissionMatrix).forEach((rolePerms) => {
                Object.values(rolePerms).forEach((actions) => {
                    if (Array.isArray(actions)) {
                        actions.filter(isVisible).forEach((action) => discoveredActions.add(action));
                    }
                });
            });

            const orderedActions = [
                ...this.actionOrder.filter(isVisible),
                ...Array.from(discoveredActions)
                    .filter((action) => !this.actionOrder.includes(action))
                    .sort(),
            ];
            const actions = orderedActions.map((action) => ({
                action,
                label: CarbonCodes.permissionAction.getLabel(String(action || "").toUpperCase()) || action,
            }));

            this.roles.forEach((role) => {
                actions.forEach((actionObj, actionIndex) => {
                    columns.push({
                        key: `${role.roleIdx}-${actionObj.action}`,
                        roleIdx: role.roleIdx,
                        roleName: role.roleName,
                        action: actionObj.action,
                        label: actionObj.label,
                        isSystem: Boolean(role.isSystem),
                        isFirstActionOfRole: actionIndex === 0,
                    });
                });
            });

            return columns;
        },
        uniqueActionsCount() {
            const isVisible = (action) => !this.hiddenActions.includes(action);
            const actionKeys = new Set(this.actionOrder.filter(isVisible));
            Object.values(this.permissionMatrix).forEach((rolePerms) => {
                Object.values(rolePerms).forEach((actions) => {
                    if (Array.isArray(actions)) {
                        actions.filter(isVisible).forEach((action) => actionKeys.add(action));
                    }
                });
            });
            return actionKeys.size;
        },
    },
    watch: {
        savingPermissions(val) {
            this.$emit("update:saving", val);
        },
        permissionMatrix: {
            handler() {
                const hasChanges = this.hasChanges();
                this.$emit("update:has-changes", hasChanges);
                this.$emit("update:change-summary", hasChanges ? this.getChangeSummary() : "");
            },
            deep: true,
        },
        originalPermissionMatrix: {
            handler() {
                const hasChanges = this.hasChanges();
                this.$emit("update:has-changes", hasChanges);
                this.$emit("update:change-summary", hasChanges ? this.getChangeSummary() : "");
            },
            deep: true,
        },
        roles: {
            handler() {
                this.loadPermissionMatrix();
            },
            deep: true,
        },
        resources: {
            handler() {
                this.loadPermissionMatrix();
            },
            deep: true,
        },
    },
    mounted() {
        if (this.roles.length && this.resources.length) {
            this.loadPermissionMatrix();
        }
    },
    methods: {
        emitChangeState() {
            const hasChanges = this.hasChanges();
            this.$emit("update:has-changes", hasChanges);
            this.$emit("update:change-summary", hasChanges ? this.getChangeSummary() : "");
        },
        loadPermissionMatrix() {
            if (!this.roles.length || !this.resources.length) return;
            this.loadingPermissions = true;
            try {
                const matrix = {};
                this.roles.forEach((role) => {
                    const perms = role.permissions || {};
                    const normalizedPermissions = {};
                    this.resources.forEach((resource) => {
                        const arr = perms[resource.name];
                        normalizedPermissions[resource.name] = Array.isArray(arr)
                            ? arr.map((a) => (a || "").toLowerCase())
                            : [];
                    });
                    matrix[role.roleIdx] = normalizedPermissions;
                });
                this.permissionMatrix = matrix;
                this.originalPermissionMatrix = JSON.parse(JSON.stringify(matrix));
                this.$nextTick(() => this.emitChangeState());
            } catch (error) {
                console.error("권한 매트릭스 로드 실패:", error);
            } finally {
                this.loadingPermissions = false;
            }
        },

        hasPermission(roleIdx, resource, action) {
            return this.permissionMatrix[roleIdx]?.[resource]?.includes(action) || false;
        },

        togglePermission(roleIdx, resource, action, event) {
            this.setPermission(roleIdx, resource, action, event.target.checked);
        },

        setPermission(roleIdx, resource, action, isChecked) {
            if (!this.permissionMatrix[roleIdx]) {
                this.permissionMatrix[roleIdx] = {};
            }
            if (!this.permissionMatrix[roleIdx][resource]) {
                this.permissionMatrix[roleIdx][resource] = [];
            }
            if (isChecked) {
                if (!this.permissionMatrix[roleIdx][resource].includes(action)) {
                    this.permissionMatrix[roleIdx][resource].push(action);
                }
            } else {
                this.permissionMatrix[roleIdx][resource] = this.permissionMatrix[roleIdx][resource].filter(
                    (a) => a !== action,
                );
            }
        },

        toggleAllPermissions(isChecked) {
            this.resources.forEach((resource) => {
                this.roles.forEach((role) => {
                    if (!role.isSystem) {
                        this.actionOrder.forEach((action) => {
                            this.setPermission(role.roleIdx, resource.name, action, isChecked);
                        });
                    }
                });
            });
        },

        toggleResourceRow(resourceName, isChecked) {
            this.roles.forEach((role) => {
                if (!role.isSystem) {
                    this.actionOrder.forEach((action) => {
                        this.setPermission(role.roleIdx, resourceName, action, isChecked);
                    });
                }
            });
        },

        isResourceRowFullySelected(resourceName) {
            const roleCount = this.roles.filter((r) => !r.isSystem).length;
            const actionCount = this.actionOrder.length;
            let selectedCount = 0;
            this.roles.forEach((role) => {
                if (!role.isSystem) {
                    this.actionOrder.forEach((action) => {
                        if (this.hasPermission(role.roleIdx, resourceName, action)) selectedCount++;
                    });
                }
            });
            return selectedCount > 0 && selectedCount === roleCount * actionCount;
        },

        isResourceRowPartiallySelected(resourceName) {
            const roleCount = this.roles.filter((r) => !r.isSystem).length;
            const actionCount = this.actionOrder.length;
            let selectedCount = 0;
            this.roles.forEach((role) => {
                if (!role.isSystem) {
                    this.actionOrder.forEach((action) => {
                        if (this.hasPermission(role.roleIdx, resourceName, action)) selectedCount++;
                    });
                }
            });
            return selectedCount > 0 && selectedCount < roleCount * actionCount;
        },

        toggleRoleColumn(roleIdx, isChecked) {
            this.resources.forEach((resource) => {
                this.actionOrder.forEach((action) => {
                    this.setPermission(roleIdx, resource.name, action, isChecked);
                });
            });
        },

        isRoleColumnFullySelected(roleIdx) {
            const resourceCount = this.resources.length;
            const actionCount = this.actionOrder.length;
            let selectedCount = 0;
            this.resources.forEach((resource) => {
                this.actionOrder.forEach((action) => {
                    if (this.hasPermission(roleIdx, resource.name, action)) selectedCount++;
                });
            });
            return selectedCount > 0 && selectedCount === resourceCount * actionCount;
        },

        isRoleColumnPartiallySelected(roleIdx) {
            const resourceCount = this.resources.length;
            const actionCount = this.actionOrder.length;
            let selectedCount = 0;
            this.resources.forEach((resource) => {
                this.actionOrder.forEach((action) => {
                    if (this.hasPermission(roleIdx, resource.name, action)) selectedCount++;
                });
            });
            return selectedCount > 0 && selectedCount < resourceCount * actionCount;
        },

        isAllSelected() {
            const roleCount = this.roles.filter((r) => !r.isSystem).length;
            const resourceCount = this.resources.length;
            const actionCount = this.actionOrder.length;
            const totalCount = roleCount * resourceCount * actionCount;
            let selectedCount = 0;
            this.resources.forEach((resource) => {
                this.roles.forEach((role) => {
                    if (!role.isSystem) {
                        this.actionOrder.forEach((action) => {
                            if (this.hasPermission(role.roleIdx, resource.name, action)) selectedCount++;
                        });
                    }
                });
            });
            return selectedCount > 0 && selectedCount === totalCount;
        },

        isPartiallySelected() {
            const roleCount = this.roles.filter((r) => !r.isSystem).length;
            const resourceCount = this.resources.length;
            const actionCount = this.actionOrder.length;
            const totalCount = roleCount * resourceCount * actionCount;
            let selectedCount = 0;
            this.resources.forEach((resource) => {
                this.roles.forEach((role) => {
                    if (!role.isSystem) {
                        this.actionOrder.forEach((action) => {
                            if (this.hasPermission(role.roleIdx, resource.name, action)) selectedCount++;
                        });
                    }
                });
            });
            return selectedCount > 0 && selectedCount < totalCount;
        },

        applyPresetToSelection(presetType) {
            const actions = this.getPresetActions(presetType);
            if (!actions) return;
            this.resources.forEach((resource) => {
                this.roles.forEach((role) => {
                    if (!role.isSystem) {
                        this.actionOrder.forEach((action) => {
                            this.setPermission(role.roleIdx, resource.name, action, false);
                        });
                        actions.forEach((action) => {
                            this.setPermission(role.roleIdx, resource.name, action, true);
                        });
                    }
                });
            });
        },

        getPresetActions(presetType) {
            switch (presetType) {
                case "read":
                    return ["read"];
                case "readWrite":
                    return ["read", "write"];
                case "full":
                    return this.actionOrder.filter((action) => action !== "full");
                default:
                    return null;
            }
        },

        getRoleDropdownItems(role) {
            return [
                { label: "전체 리소스에 읽기만", onClick: () => this.applyRolePreset(role.roleIdx, "read") },
                { label: "전체 리소스에 읽기+쓰기", onClick: () => this.applyRolePreset(role.roleIdx, "readWrite") },
                { label: "전체 리소스에 전체 권한", onClick: () => this.applyRolePreset(role.roleIdx, "full") },
                { label: "권한 초기화", onClick: () => this.clearRolePermissions(role.roleIdx) },
                {
                    label: "권한 복사",
                    disabled: !this.hasRolePermissions(role.roleIdx),
                    onClick: () => this.copyRolePermissions(role.roleIdx),
                },
                {
                    label: "권한 붙여넣기",
                    disabled: !this.copiedRolePermissions,
                    onClick: () => this.pasteRolePermissions(role.roleIdx),
                },
            ];
        },

        applyRolePreset(roleIdx, presetType) {
            const actions = this.getPresetActions(presetType);
            if (!actions) return;
            this.resources.forEach((resource) => {
                this.actionOrder.forEach((action) => {
                    this.setPermission(roleIdx, resource.name, action, false);
                });
                actions.forEach((action) => {
                    this.setPermission(roleIdx, resource.name, action, true);
                });
            });
        },

        clearRolePermissions(roleIdx) {
            this.resources.forEach((resource) => {
                this.actionOrder.forEach((action) => {
                    this.setPermission(roleIdx, resource.name, action, false);
                });
            });
        },

        copyRolePermissions(roleIdx) {
            const permissions = this.permissionMatrix[roleIdx] || {};
            this.copiedRolePermissions = JSON.parse(JSON.stringify(permissions));
            this.toastSuccess("권한이 복사되었습니다.");
        },

        pasteRolePermissions(roleIdx) {
            if (!this.copiedRolePermissions) {
                this.toastError("복사된 권한이 없습니다.");
                return;
            }
            this.resources.forEach((resource) => {
                this.actionOrder.forEach((action) => {
                    this.setPermission(roleIdx, resource.name, action, false);
                });
                const copiedActions = this.copiedRolePermissions[resource.name] || [];
                if (Array.isArray(copiedActions)) {
                    copiedActions.forEach((action) => {
                        this.setPermission(roleIdx, resource.name, action, true);
                    });
                }
            });
            this.toastSuccess("권한이 붙여넣기되었습니다.");
        },

        hasRolePermissions(roleIdx) {
            const permissions = this.permissionMatrix[roleIdx] || {};
            return (
                Object.keys(permissions).length > 0 &&
                Object.values(permissions).some((actions) => Array.isArray(actions) && actions.length > 0)
            );
        },

        getResourceDropdownItems(resource) {
            return [
                { label: "모든 역할에 읽기만", onClick: () => this.applyResourcePreset(resource.name, "read") },
                { label: "모든 역할에 읽기+쓰기", onClick: () => this.applyResourcePreset(resource.name, "readWrite") },
                { label: "모든 역할에 전체 권한", onClick: () => this.applyResourcePreset(resource.name, "full") },
                { label: "권한 초기화", onClick: () => this.clearResourcePermissions(resource.name) },
            ];
        },

        applyResourcePreset(resourceName, presetType) {
            const actions = this.getPresetActions(presetType);
            if (!actions) return;
            this.roles.forEach((role) => {
                if (!role.isSystem) {
                    this.actionOrder.forEach((action) => {
                        this.setPermission(role.roleIdx, resourceName, action, false);
                    });
                    actions.forEach((action) => {
                        this.setPermission(role.roleIdx, resourceName, action, true);
                    });
                }
            });
        },

        clearResourcePermissions(resourceName) {
            this.roles.forEach((role) => {
                if (!role.isSystem) {
                    this.actionOrder.forEach((action) => {
                        this.setPermission(role.roleIdx, resourceName, action, false);
                    });
                }
            });
        },

        isChanged(roleIdx, resourceName, action) {
            const original = this.originalPermissionMatrix[roleIdx]?.[resourceName] || [];
            const current = this.permissionMatrix[roleIdx]?.[resourceName] || [];
            const origArr = Array.isArray(original) ? original : [];
            const currArr = Array.isArray(current) ? current : [];
            return origArr.includes(action) !== currArr.includes(action);
        },

        hasChanges() {
            for (const role of this.roles) {
                for (const resource of this.resources) {
                    for (const action of this.actionOrder) {
                        if (this.isChanged(role.roleIdx, resource.name, action)) return true;
                    }
                }
            }
            return false;
        },

        getChangeSummary() {
            let changedCount = 0;
            const changedRoles = new Set();
            for (const role of this.roles) {
                for (const resource of this.resources) {
                    for (const action of this.actionOrder) {
                        if (this.isChanged(role.roleIdx, resource.name, action)) {
                            changedCount++;
                            changedRoles.add(role.roleName);
                        }
                    }
                }
            }
            if (changedCount === 0) return "";
            return `${changedCount}개 권한 변경됨 (${Array.from(changedRoles).join(", ")})`;
        },

        async savePermissionMatrix() {
            this.savingPermissions = true;
            try {
                let updatedCount = 0;
                for (const role of this.roles) {
                    const matrixRow = this.permissionMatrix[role.roleIdx] || {};
                    const updatedPermissions = Object.fromEntries(
                        Object.entries(matrixRow).map(([res, arr]) => [
                            res,
                            (Array.isArray(arr) ? arr : []).map((a) => (a || "").toUpperCase()),
                        ]),
                    );
                    const originalSerialized = JSON.stringify(role.permissions || {});
                    const updatedSerialized = JSON.stringify(updatedPermissions);
                    if (originalSerialized === updatedSerialized) continue;

                    const { data } = await carbonApi.accessControl.updateRole(role.roleCode, {
                        permissions: updatedPermissions,
                    });
                    if (data?.success) updatedCount += 1;
                }

                if (updatedCount > 0) {
                    this.toastSuccess("권한 매트릭스가 저장되었습니다.");
                    this.originalPermissionMatrix = JSON.parse(JSON.stringify(this.permissionMatrix));
                    this.$emit("refresh");
                } else {
                    this.toastSuccess("변경된 권한이 없습니다.");
                }
            } catch (error) {
                console.error("권한 매트릭스 저장 실패:", error);
                this.toastError(this.getErrorMessage(error, "권한 매트릭스 저장에 실패했습니다."));
            } finally {
                this.savingPermissions = false;
            }
        },
    },
};
</script>

<style scoped>
.tab-content {
    padding-top: 8px;
}
.matrix-scroll-container {
    max-height: 400px;
    position: relative;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}
.matrix-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 12px;
}
.matrix-table .col-resource {
    width: 170px;
}
.matrix-table thead {
    position: sticky;
    top: 0;
    z-index: 20;
    background: #e8f5ee;
    color: #1e293b;
}
.matrix-table th {
    padding: 10px 8px;
    text-align: center;
    font-weight: 600;
    border: none;
    border-bottom: 2px solid #cbd5e1;
    white-space: nowrap;
}
.matrix-table .sticky-col {
    position: sticky;
    left: 0;
    background: #e8f5ee;
    z-index: 21;
    width: 170px;
    min-width: 170px;
    max-width: 170px;
    box-sizing: border-box;
}
.select-all-container {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}
.select-all-checkbox {
    cursor: pointer;
    width: 18px;
    height: 18px;
    accent-color: #52b788;
}
.select-all-label {
    font-weight: 600;
    color: #1e293b;
}
.role-select-container {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
}
.role-select-checkbox {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: #52b788;
}
.role-header-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}
.role-dropdown-wrapper {
    position: relative;
    flex-shrink: 0;
}
.role-dropdown-btn {
    padding: 4px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #ffffff;
    color: #64748b;
    cursor: pointer;
    font-size: 12px;
}
.role-dropdown-btn:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
}
.resource-select-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}
.resource-select-checkbox {
    cursor: pointer;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    accent-color: #52b788;
}
.resource-header-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}
.resource-dropdown-wrapper {
    position: relative;
    flex-shrink: 0;
    z-index: 1100;
}
.resource-dropdown-btn {
    padding: 4px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #ffffff;
    color: #64748b;
    cursor: pointer;
    font-size: 12px;
}
.resource-dropdown-btn:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
}
.matrix-table .role-header {
    background: #f0fdf4;
    font-size: 12px;
    font-weight: 600;
}
.matrix-table .action-header {
    background: #f8fafc;
    font-size: 11px;
    font-weight: 500;
    color: #475569;
    /* 액션 라벨이 좁은 컬럼에서 겹치지 않도록 헤더만 줄바꿈 허용 */
    white-space: normal;
    word-break: keep-all;
    line-height: 1.15;
}
.matrix-table .action-header-first {
    border-left: 2px solid #e2e8f0;
    padding-left: 10px;
}
.matrix-table tbody td {
    padding: 8px;
    border: none;
    border-bottom: 1px solid #e2e8f0;
}
.matrix-table .resource-name {
    position: sticky;
    left: 0;
    background: #f8fafc;
    font-weight: 600;
    border-right: 2px solid #cbd5e1;
    z-index: 15;
    padding: 10px 12px;
    overflow: visible;
}
.matrix-table .permission-cell {
    text-align: center;
    background: #ffffff;
}
.matrix-table .permission-cell input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: #52b788;
}
.matrix-table .permission-cell.changed {
    background: #eff6ff !important;
    position: relative;
}
.matrix-table .permission-cell.changed::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid #3b82f6;
    border-radius: 2px;
    pointer-events: none;
}
</style>
